import { CircularProgress, LinearProgress, Typography } from "@mui/material";
import lodash from "lodash";
import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../BrandOnBoarding/api";
import Modal from "../../Component/Modal/Modal";
import MUIAutocomplete from "../../Component/UI/MUIAutocomplete/MUIAutocomplete";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import RadioGroup from "../../Component/UI/RadioGroup/RadioGroup";
import Warning from "../../Component/Warning/Warning";
import Alert from "../../Component/UI/Alert/Alert";
import { useSelector, useDispatch } from "react-redux";
import { validateBrandInputValue, validateProductInputValue, validateCategoryInputValue } from "../../utils/helpers";
import { getCategoryPrompt } from "../../utils/prompt";
import Suggestions from "../CategoryDiscovery/Suggestions/Suggestions";
import TextInput from "../../Component/UI/TextInput/TextInput";
import AttributeInput from "../CategoryDiscovery/AttributeInput/AttributeInputV2";
import classes from "./Form.module.css";
import ProductVariant from "./ProductVariant";
import UserKeyPlayer from "./UserKeyPlayer";
import { DiscoveryContext } from "./discovery-context";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS, getEntityLabel } from "../../utils/labelMapping";
import useCountry from "./useCountry";
import useGeneratePreCategoryDimensions from "./useGeneratePreCategoryDimensions";
import useGenerateProductTaxonomies from "./useGenerateProductTaxonomies";
import useProductPreference from "./useProductPreference";
import useTopUserProducts from "./useTopUserProducts";
import { Checkbox, FormControlLabel } from "@mui/material";
import useHistory from "./History/useHistory";
import { getCategoryTypePrompt } from "../../utils/prompts/prompts.en";
import MUIMultiSelect from "../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import { classNames } from "@react-pdf-viewer/core";
import History from "../DiscoveryV2/History/HistoryV2";
import MUIIconButton from "../../Component/UI/MUIIconButton/MUIIconButton";
import { SettingsOutlined } from "@mui/icons-material";
import DiscoverySettingsModal from "./DiscoverySetting/Setting";
import userMetricSettingCreation from "../Set/userMetricSettingCreation";
import { DefaultSet } from "../../utils/constant";
import { isCompositeIndexScoreType } from "../../utils/brandIndexCalc";
import { resolveDefaultKeywordOptions } from "../../utils/keywordOptions";
import { hasBatchCapableLlm } from "../../utils/batchCapableLlms";



const DISCOVERY_MODE_OPTIONS = [
   { value: "baseline", name: "Discovery Test" },
  { value: "full", name: "Full Discovery" },
];

function isSearchLLM(value = "") {
  const v = value.toLowerCase();
  return v.includes("search") || v.includes("perplexity");
}

const BASELINE_LLM_FALLBACK_ORDER = ["gpt-6-luna", "gpt-4.1-nano"];

function getBaselineLLMOptions(llmOptions) {
  const options = llmOptions || [];
  for (const value of BASELINE_LLM_FALLBACK_ORDER) {
    const match = options.find((opt) => opt.value === value);
    if (match) return [match];
  }
  return [];
}

export default function Form({
  onSubmit,
  onTypeChanged = () => { },
  onReset = () => { },
  warningText,
  setWarningText = () => { },
  cancelTransaction,
  stopPolling = () => { },
  startPolling = () => { },
  setCancelTransaction = () => { },
  setIsHistoryRefresh = () => { },
  setPage = () => { },
  page,
  isHistoryRefresh = false,
  discoveryMode = "full",
  onDiscoveryModeChange = () => { },
  isBaselineDiscoveryEnabled = false,
  onBaselineHtmlReportChange = () => { },
}) {
  const discoveryCtx = useContext(DiscoveryContext);
  const { user, selectedCustomerId, customerUiInfo } = useContext(AuthContext);
  const dispatch = useDispatch();
  const { data: llmOptions, loading: llmOptionsLoading } = useSelector((state) => state.llmOptions);
  const getDefaultLLM = (mode = discoveryMode) => {
    if (mode !== "baseline") return "gpt-5.6";
    const baselineOptions = getBaselineLLMOptions(llmOptions);
    return baselineOptions[0]?.value || BASELINE_LLM_FALLBACK_ORDER[0];
  };
  const userMetricSetting = useSelector((store) => store.userSet);
  const defaultMetricSettingId = userMetricSetting?.brandAuditMetricSettingId ?? DefaultSet;
  const { customIndexScoreTypes } = customerUiInfo || {};
  const hasCustomIndexScoreTypes =
    Array.isArray(customIndexScoreTypes) && customIndexScoreTypes.length > 0;
  // Always resolves to the customer's default (e.g. "Accessibility") when
  // they have one configured — there is no manual picker for it anymore.
  const defaultCustomIndexScoreType = hasCustomIndexScoreTypes ? customIndexScoreTypes[0] : "";
  const showUserMetricSettingModel = useSelector((store) => store.userSet.showModel);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showManualKeyPlayerInput, setShowManualKeyPlayerInput] =
    useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const competitorsRef = useRef(discoveryCtx.competitors);

  const [brand, setBrand] = useState("");
  // const [discoveryType, setDiscoveryType] = useState(discoveryCtx.type || "brand");
  const [llm, setLLM] = useState(getDefaultLLM());
  const [category, setCategory] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [market, setMarket] = useState("");
  const [selectedBrandsProducts, setSelectedBrandProducts] = useState([]);
  const [prefLoading, setPrefLoading] = useState(false);
  const [showManualCategoryInput, setShowManualCategoryInput] = useState(false);
  const [showManualCategoryTypeInput, setShowManualCategoryTypeInput] = useState(false);
  const [manualCategoryInput, setManualCategoryInput] = useState("");
  const [categoryModalLoading, setCategoryModalLoading] = useState(false);
  const [categoryValidationError, setCategoryValidationError] = useState("");
  const [categoryValidationSuggestion, setCategoryValidationSuggestion] = useState("");
  const [manualCategoryTypeInput, setManualCategoryTypeInput] = useState("");
  const [aICategories, setAICategories] = useState([]);
  const [aICategoryTypes, setAICategoryTypes] = useState([{ name: "Add Manually", value: "Add Manually" }]);
  const [isMock, setIsMock] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [metricSettingId, setMetricSettingId] = useState(defaultMetricSettingId);
  // No manual picker in Discovery — always default to the customer's
  // custom index score type (e.g. "Accessibility") when they have one.
  const [customIndexScoreType, setCustomIndexScoreType] = useState(defaultCustomIndexScoreType);
  const [rankCompetitiveSet, setRankCompetitiveSet] = useState([]);
  const [rankMethodology, setRankMethodology] = useState("by_category");
  const [keywords, setKeywords] = useState(() => resolveDefaultKeywordOptions(customerUiInfo));
  const [isBatchMode, setIsBatchMode] = useState(false);
  useEffect(() => {
    if (!hasBatchCapableLlm(llm)) setIsBatchMode(false);
  }, [llm]);
  const [isNonBrandedQuestions, setIsNonBrandedQuestions] = useState(false);
  const [isFactBasedQuestions, setIsFactBasedQuestions] = useState(false);
  const [brandDomain, setBrandDomain] = useState("");
  const [isCategorySuggestionLoading, setCategorySuggestionLoading] =
    useState(false);
  const [isCategoryTypeSuggestionLoading, setCategoryTypeSuggestionLoading] =
    useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const categoryChangeTimerRef = useRef();
  const keyPlayerChangeTimerRef = useRef();
  const llmRef = useRef(llm);
  useEffect(() => { llmRef.current = llm; }, [llm]);

  useEffect(() => {
    if (discoveryMode !== "baseline" || discoveryCtx.state.id) return;
    const baselineOptions = getBaselineLLMOptions(llmOptions);
    if (!baselineOptions.some((opt) => opt.value === llmRef.current)) {
      setLLM(getDefaultLLM("baseline"));
    }
  }, [discoveryMode, llmOptions, discoveryCtx.state.id]);
  const discoveryType = discoveryCtx?.type || "brand"
  const {
    loading: keyPlayersLoading,
    keyPlayers,
    setKeyPlayers,
    getBrandOrProductSuggestions,
  } = useTopUserProducts();
  const {
    deleteHistory,
  } = useHistory(discoveryType);
  const { getProductPrefs } = useProductPreference();
  const { generateProductTaxonomies } = useGenerateProductTaxonomies();
  const { generatePreCategoryDimensions } = useGeneratePreCategoryDimensions();
  const discoveryTransactionId = discoveryCtx.state.id;
  const discoveryTransactionIdRef = useRef(discoveryTransactionId);
  useEffect(() => { discoveryTransactionIdRef.current = discoveryTransactionId; }, [discoveryTransactionId]);

  // customerUiInfo can arrive after this component's first render, so the
  // useState initializer above may have locked in "" — catch it once it's
  // available. Skipped while viewing/loading an existing discovery so we
  // don't stomp on a restored (possibly empty) historical value.
  useEffect(() => {
    if (!discoveryTransactionId && hasCustomIndexScoreTypes && !customIndexScoreType) {
      setCustomIndexScoreType(defaultCustomIndexScoreType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCustomIndexScoreTypes, defaultCustomIndexScoreType]);
  const timerRef = useRef();
  const abortControllerRef = useRef(null);
  const categoryValidationAbortRef = useRef(null);
  const RADIO_OPTIONS = [
    {
      value: "brand",
      label: ENTITY_LABELS.brand,
    },
    {
      value: "product",
      label: ENTITY_LABELS.product,
    },
  ];

  const updatedKeyPlayers = (() => {
    const apiPlayers = keyPlayers.map((player) => ({
      ...player,
      value: player.name,
      name: player.name,
    }));
    const existingSelected = selectedBrandsProducts
      .filter((s) => !apiPlayers.some((p) => p.name === s.name))
      .map((s) => ({ ...s, value: s.name }));
    const merged = [...existingSelected, ...apiPlayers];
    return merged.length > 0
      ? [
          ...merged,
          { name: "Add Manually", domain: "", value: "addManually", id: 0 },
        ]
      : [];
  })();

  let hasRequiredCompetitors =
    discoveryType === "category"
      ? discoveryCtx.competitors.length > 0
      : discoveryCtx.competitors.length >= 1;

  useEffect(() => {
    if (discoveryCtx?.state?.transactionId) return;
    if (hasCustomIndexScoreTypes) return;
    setMetricSettingId(defaultMetricSettingId);
  }, [defaultMetricSettingId, hasCustomIndexScoreTypes]);

  //       useEffect(()=>{
  // setDiscoveryType(discoveryCtx?.type)
  //       },[discoveryCtx?.type])

  useEffect(() => {
    if (!category || discoveryTransactionId) return;
    if (categoryChangeTimerRef.current) {
      clearTimeout(categoryChangeTimerRef.current);
    }
    discoveryCtx.updateProductPrefs([]);
    categoryChangeTimerRef.current = setTimeout(() => {
      if (discoveryCtx.type === "category" || discoveryCtx.type === "product") {
        generateProductTaxonomies(category, llmRef.current);
        setPrefLoading(true);
        if (discoveryCtx.type === "category") {
          getProductPrefs()
            .then(() => setPrefLoading(false))
            .catch(() => setPrefLoading(false));
        }
      }
      // generatePreCategoryDimensions(category);
    }, 1000);
  }, [category, brand]);

  useEffect(() => {
    if (!brand || discoveryCtx.is_launched || discoveryCtx.is_resetFormState || aICategories.length > 0) {
      // setAICategories([]);
      return;
    }
    async function getCategoryAISuggestions() {
      setCategorySuggestionLoading(true);
      let language = discoveryCtx.language;
      try {
        const prompt = getCategoryPrompt(brand, language);
        const payload = {
          llm: llmRef.current,
          prompt: prompt,
        };

        let response = await api.post(`/llm-execute`, payload);
        const result = response?.data?.data || [];

        if (result) {
          let resultArray = result;
          const categoryList = resultArray.categories.map((o) => ({ name: o, value: o }));
          if (category && !categoryList.some((c) => c.value === category)) {
            categoryList.unshift({ name: category, value: category });
          }
          setAICategories([
            ...categoryList,
            {
              name: "Add Manually",
              value: "Add Manually",
            },
          ]);

        }
      } catch (error) {
        console.log(error);
      } finally {
        setCategorySuggestionLoading(false);
      }
    }

    const debouncedFunction = lodash.debounce(getCategoryAISuggestions, 2000);
    debouncedFunction();
    return () => debouncedFunction.cancel();
  }, [brand, discoveryCtx.is_launched, discoveryCtx.language]);

  // useEffect(() => {
  //   if (!brand || discoveryCtx.is_launched || !category || discoveryCtx.is_resetFormState || aICategoryTypes.length > 0) {
  //     // setAICategoryTypes([]);
  //     return;
  //   }
  //   async function getCategoryTypeAISuggestions() {
  //     setCategoryTypeSuggestionLoading(true);
  //     let language = discoveryCtx.language;
  //     console.log("language", language);
  //     try {
  //       const prompt = getCategoryTypePrompt(brand, category, language);
  //       const payload = {
  //         llm: llmRef.current,
  //         prompt: prompt,
  //       };
  //
  //       let response = await api.post(`/llm-execute`, payload);
  //       const result = response?.data?.data || [];
  //
  //       if (result) {
  //         let resultArray = result;
  //         const categoryTypeList = resultArray.categoryTypes.map((o) => ({ name: o, value: o }));
  //         if (categoryType && !categoryTypeList.some((c) => c.value === categoryType)) {
  //           categoryTypeList.unshift({ name: categoryType, value: categoryType });
  //         }
  //         setAICategoryTypes([
  //           ...categoryTypeList,
  //           {
  //             name: "Add Manually",
  //             value: "Add Manually",
  //           }
  //         ]);
  //
  //       }
  //     } catch (error) {
  //       console.log(error);
  //     } finally {
  //       setCategoryTypeSuggestionLoading(false);
  //     }
  //   }
  //
  //   const debouncedFunction = lodash.debounce(getCategoryTypeAISuggestions, 2000);
  //   debouncedFunction();
  //   return () => debouncedFunction.cancel();
  // }, [brand, discoveryCtx.is_launched, category]);

  useEffect(() => {
    if (!category || discoveryTransactionIdRef.current || !brand || cancelTransaction || discoveryCtx.is_resetFormState) {
      return;
    }
    if (keyPlayerChangeTimerRef.current) {
      clearTimeout(keyPlayerChangeTimerRef.current);
    }
    discoveryCtx.updatedCompetitors(
      discoveryCtx.competitors.filter(
        (comp) =>
          comp &&
          comp?.name.toString().toLowerCase() ===
          discoveryCtx.entity.toString().toLowerCase()
      )
    );
    keyPlayerChangeTimerRef.current = setTimeout(() => {
      if (discoveryCtx.type === "category" || discoveryCtx.type === "product")
        getBrandOrProductSuggestions(
          "product",
          category,
          null,
          [],
          null,
          market,
          brand
        );
      if (discoveryCtx.type === "brand")
        getBrandOrProductSuggestions(
          "brand",
          category,
          null,
          [],
          null,
          market,
          brand
        );
    }, 1000);
  }, [brand, discoveryType, category, market]);


  function handleProductBrandSelection(players, options) {
    // CASE 1: User added manually
    // So add that object into keyPlayers list
    if (!Array.isArray(players)) {
      const manualObj = players; // your object containing id/name/domain

      setKeyPlayers(prev => {
        const exists = prev.some(p => p.name === manualObj.name);
        if (!exists) {
          return [
            ...prev,
            manualObj
          ];
        }
        return prev;
      });
      if (selectedBrandsProducts?.length >= 5) {
         toast.warn("Only 5 competitors are allowed");
          return;
      }
      // ALSO update selected competitor list
      setSelectedBrandProducts(prev => {
        const exists = prev.some(p => p.name === manualObj.name);
        if (!exists) {
          const updated = [...prev, manualObj];
          discoveryCtx.updatedCompetitors(updated);
          return updated;
        }
        return prev;
      });

      return;
    }

    // CASE 2: Normal selection (players is array)
    const maxProductBrands = 5;
    const message = "Only 5 competitors are allowed";
    setSelectedBrandProducts((prevPlayers) => {
      let updatedPlayers = players.map(player => {
        const match = options.find(item => item.value === player);
        return match ? match : { value: player, name: player, domain: "example.com" };
      });

      if (updatedPlayers.length > maxProductBrands) {
        toast.warn(message);
        updatedPlayers = updatedPlayers.slice(0, maxProductBrands);
        discoveryCtx.updatedCompetitors(updatedPlayers);
        return prevPlayers;
      }

      discoveryCtx.updatedCompetitors(updatedPlayers);
      return updatedPlayers;
    });
  }

  function handleDeleteBrandProduct(itemName) {
    setSelectedBrandProducts((prevBrandProducts) => {
      return prevBrandProducts.filter((item) => item.name != itemName);
    });
    const updatedCompetitors = discoveryCtx.competitors.filter(
      (comp) =>
        comp.name.toString().toLowerCase() !== itemName.toString().toLowerCase()
    );
    discoveryCtx.updatedCompetitors(updatedCompetitors);
  }

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      setSelectedBrandProducts([]);
    }
  }, [discoveryCtx.abort_launch, discoveryCtx.is_launched]);

  useEffect(() => {
    if (selectedBrandsProducts.length > 0) return;
    if (discoveryTransactionId) {
      setKeyPlayers([]);
      return;
    }
    if (keyPlayers.length === 8 && discoveryType !== "category") {
      const updatedCompetitors = [...discoveryCtx.competitors].concat(
        keyPlayers.slice(0, 3)
      );
      discoveryCtx.updatedCompetitors(updatedCompetitors);
      setSelectedBrandProducts([...keyPlayers.slice(0, 3)]);
    }
  }, [
    keyPlayers,
    discoveryType,
    discoveryCtx.category,
    discoveryTransactionId,
  ]);

  useEffect(() => {
    competitorsRef.current = discoveryCtx.competitors;
  }, [discoveryCtx.competitors]);

  async function checkIfInputValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let response = await validateBrandInputValue(input, controller.signal, category);
      setLoading(false);
      // Only continue if this request wasn’t aborted
      if (controller.signal.aborted) return;
      const domain = response?.data?.data?.domain || "example.com";
      setBrandDomain(domain)
      if (response?.data?.data?.ans !== "Yes") {
        setWarningText(
          `The ${discoveryType} or category name appears to be incorrect. This may impact the ${ENTITY_LABELS.brandIndexScore}.`
        );
        setSuggestions(response?.data?.data?.correct_inputs ?? []);
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  async function checkIfProductValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");
    setProductVariants([]);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let response = await validateProductInputValue(input, controller.signal, category);
      setLoading(false);
      if (controller.signal.aborted) return;
      const responseData = response?.data;
      const variants = responseData?.data?.variants || [];
      const updatedVariants = variants.map((variant) => {
        return {
          id: variant,
          name: variant,
          active: true,
        };
      });
      setProductVariants(updatedVariants);
      setBrandDomain(responseData.data.domain)

      if (responseData.data.ans !== "Yes") {
        if (
          new String(responseData?.data?.correct_inputs[0])
            .toLocaleLowerCase()
            .trim() !== new String(discoveryCtx.entity).toLowerCase().trim()
        ) {
          setSuggestions(responseData?.data?.correct_inputs);
          setWarningText(
            `The ${discoveryType} or category name appears to be incorrect. This may impact the ${ENTITY_LABELS.brandIndexScore}.`
          );
        }
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  async function checkIfCategoryValid(categoryValue, brandValue, discoveryTypeValue) {
    if (!categoryValue?.trim()) {
      setCategoryValidationError("");
      setCategoryValidationSuggestion("");
      return;
    }
    if (categoryValidationAbortRef.current) {
      categoryValidationAbortRef.current.abort();
    }
    const controller = new AbortController();
    categoryValidationAbortRef.current = controller;
    setCategoryModalLoading(true);
    setCategoryValidationError("");
    setCategoryValidationSuggestion("");

    try {
      const response = await validateCategoryInputValue(
        categoryValue,
        controller.signal,
        brandValue,
        discoveryTypeValue === "product" ? "product" : "brand"
      );
      if (controller.signal.aborted) return;
      const data = response?.data?.data;
      if (data?.ans !== "Yes") {
        setCategoryValidationError(
          `This category doesn't appear to match ${brandValue}. Please review it to ensure accurate ${ENTITY_LABELS.brandIndexScore} results.`
        );
        setCategoryValidationSuggestion(data?.correct_inputs?.[0] || "");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.log("Error while validating category", error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setCategoryModalLoading(false);
      }
    }
  }

  const debouncedCategoryValidation = useRef(
    lodash.debounce((value, brandValue, discoveryTypeValue) => {
      checkIfCategoryValid(value, brandValue, discoveryTypeValue);
    }, 1000)
  ).current;

  function handleSuggestionClick(text) {
    discoveryCtx.updatedCompetitors([]);
    setBrand(text);
    setSuggestions([]);
    setSelectedBrandProducts([]);
  }

  function handleBrandInputChange(e) {
    const value = e.target.value;
    setBrand(value);
    resetState();
    setCategory("");
    setCategoryType("");
    setAICategories([]);
    setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
  }
  function resetState() {
    discoveryCtx.updateAbortLaunch(true);
    setProductVariants([]);
    discoveryCtx.updateId(null);
    discoveryCtx.updatedLaunched(false);
  }
  function handleDiscoveryTypeChange(value) {
    discoveryCtx.resetState();
    discoveryCtx.updateLanguage("en");
    setPage(1);
    stopPolling();
    onTypeChanged(value);
    discoveryCtx.updateType(value);
    // setDiscoveryType(value);
    setBrand("");
    setCategory("");
    setCategoryType("");
    setAICategories([]);
    setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
    setLLM(getDefaultLLM());
    setSuggestions([]);
    setProductVariants([]);
    setKeyPlayers([]);
    setSelectedBrandProducts([]);
    onReset();
    setMarket("");
  }

  function deleteTransaction() {
    setIsHistoryRefresh(prev => !prev);
    discoveryCtx.resetState();
    stopPolling();
    setCategory("");
    setCategoryType("");
    deleteHistory(discoveryCtx?.state?.id);
    setShowDeleteDialog(false);
    setProductVariants([]);
    setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
    setBrand("");
    setLLM(getDefaultLLM());
    setAICategories([])
    setKeyPlayers([]);
    setSelectedBrandProducts([]);
    onReset();
    setMarket("");
  }



  const industryChangeRef = useRef(false);
  useEffect(() => {
    if (!industryChangeRef.current) {
      industryChangeRef.current = true;
      return;
    }
    setIsHistoryRefresh((prev) => !prev);
    if (discoveryCtx.is_launched) {
      cancelTransactionRecord();
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    if (
      !brand ||
      discoveryCtx.is_launched ||
      discoveryTransactionId
      || cancelTransaction ||
      discoveryCtx.is_resetFormState
    ) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (discoveryType === "product") {
        checkIfProductValid(brand);
      } else {
        checkIfInputValid(brand);
      }
    }, 1000);
  }, [
    brand
  ]);


  useEffect(() => {
    if (discoveryTransactionId) {
      setBrand(discoveryCtx.entity);
      setAICategories([
        {
          name: discoveryCtx.category,
          value: discoveryCtx.category,
        },
      ]);
      const resolvedCategoryType = discoveryCtx.categoryType || "companies";
      setAICategoryTypes([
        {
          name: resolvedCategoryType,
          value: resolvedCategoryType,
        },
        {
          name: "Add Manually",
          value: "Add Manually",
        },
      ]);
      setCategory(discoveryCtx.category);
      setCategoryType(discoveryCtx.categoryType);
      setMarket(discoveryCtx.market);
      setLLM(discoveryCtx.llm);
      setSelectedBrandProducts(discoveryCtx.competitors.slice(1));
    }
  }, [
    discoveryTransactionId,
    discoveryCtx.category,
    // discoveryCtx.llm,
    discoveryCtx.entity,
    discoveryCtx.market,
    discoveryCtx.competitors,
  ]);

  useEffect(() => {
    if (!discoveryTransactionId) return;
    setMetricSettingId(discoveryCtx.customTemplateID ?? "default");
    if (discoveryCtx.keywords?.length > 0) setKeywords(discoveryCtx.keywords);
    setCustomIndexScoreType(discoveryCtx.customIndexScoreType ?? "");
    setRankCompetitiveSet(discoveryCtx.rankCompetitiveSet ?? []);
    setRankMethodology(discoveryCtx.rankMethodology ?? "by_category");
    setIsBatchMode(!!discoveryCtx.isBatchMode);
    setIsNonBrandedQuestions(!!discoveryCtx.isNonBrandedQuestions);
    setIsFactBasedQuestions(!!discoveryCtx.isFactBasedQuestions);
  }, [discoveryTransactionId, discoveryCtx.customTemplateID, discoveryCtx.keywords, discoveryCtx.customIndexScoreType, discoveryCtx.rankCompetitiveSet, discoveryCtx.rankMethodology, discoveryCtx.isBatchMode, discoveryCtx.isNonBrandedQuestions, discoveryCtx.isFactBasedQuestions]);

  useEffect(() => {
    if ((cancelTransaction && !discoveryCtx.abort_launch) || discoveryCtx.is_resetFormState) {
      stopPolling();
      discoveryCtx.resetState();
      discoveryCtx.updateLanguage("en");
      setBrand(discoveryCtx.entity);
      setCategory(discoveryCtx.category);
      setCategoryType(discoveryCtx.categoryType);
      setMarket(discoveryCtx.market);
      setLLM(discoveryCtx.llm);
      setCancelTransaction(false);
      setAICategories([]);
      setKeyPlayers([]);
      setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
      setSelectedBrandProducts([]);
      setMetricSettingId(defaultMetricSettingId);
      setKeywords(resolveDefaultKeywordOptions(customerUiInfo));
      setCustomIndexScoreType(defaultCustomIndexScoreType);
      setRankCompetitiveSet([]);
      discoveryCtx.resetFormState(false);
    }
  }, [cancelTransaction, discoveryCtx.abort_launch, discoveryCtx.is_resetFormState])

  const cancelTransactionRecord = () => {
    setCancelTransaction(true);
    resetState();
    onDiscoveryModeChange(isBaselineDiscoveryEnabled ? "baseline" : "full");
  };

  const isUrlFromDifferentUser = (() => {
    const _uid = new URLSearchParams(window.location.search).get("user_id");
    return !!_uid && String(user?.user_role?.user_id) !== String(_uid);
  })();

  const handleStartNew = () => {
    stopPolling();
    if (discoveryCtx.is_launched) {
      // User already launched — keep selected values, refresh option lists
      setAICategories([]);
      setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
      // if (!categoryType) {
      //   setCategoryType("companies");
      // }
      if (brand && category) {
        setKeyPlayers([]);
        const type = discoveryCtx.type === "brand" ? "brand" : "product";
        getBrandOrProductSuggestions(type, category, null, [], null, market, brand);
      }
      discoveryCtx.resetForRelaunch();
      if (llmOptions?.length && !llmOptions.some((opt) => opt.value === llm)) {
        setLLM(getDefaultLLM());
      }
      const templateBelongsToUser =
        metricSettingId === "default" ||
        (userMetricSetting?.data ?? []).some((t) => String(t.id) === String(metricSettingId));
      if (isUrlFromDifferentUser || !templateBelongsToUser) {
        discoveryCtx.updateCustomTemplateID(null);
        setMetricSettingId(defaultMetricSettingId);
        setCustomIndexScoreType(defaultCustomIndexScoreType);
        setRankCompetitiveSet([]);
      }
    } else {
      // Not launched yet — clear the form entirely
      discoveryCtx.resetForRelaunch();
      setBrand("");
      setCategory("");
      setCategoryType("");
      setMarket("");
      setLLM(getDefaultLLM());
      setAICategories([]);
      setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
      setKeyPlayers([]);
      setSelectedBrandProducts([]);
      discoveryCtx.updateCustomTemplateID(null);
      setMetricSettingId(defaultMetricSettingId);
      setCustomIndexScoreType(defaultCustomIndexScoreType);
      setRankCompetitiveSet([]);
    }

    // onDiscoveryModeChange(isBaselineDiscoveryEnabled ? "baseline" : "full");
  };
  const debounceLaunch = lodash.debounce(function () {
    if ((!brand && discoveryType !== "category") || !category || !llm
      //  || !categoryType
    ) {
      toast.warn("Please fill all required fields");
      return;
    }

    if (
      discoveryType === "category" &&
      (!discoveryCtx.location ||
        !discoveryCtx.occupation ||
        discoveryCtx.attributes.length === 0)
    ) {
      toast.warn("Please fill all required fields");
      return;
    }

    const competitors = [
      {
        name: brand,
        domain: brandDomain,
        variants: productVariants,
      },
      ...(discoveryCtx?.competitors ?? [])
    ];

    const categoryWiseMetric = category ? { [category]: metricSettingId } : {};

    onSubmit({
      brand,
      category,
      categoryType,
      market,
      llm,
      discoveryType,
      discoveryMode,
      isMock,
      competitors,
      metricSettingId,
      keywords,
      categoryWiseMetric,
      customIndexScoreType,
      isBatchMode,
      isNonBrandedQuestions,
      isFactBasedQuestions,
      rankMethodology,
      rankCompetitiveSet: (isCompositeIndexScoreType(customIndexScoreType) || !!user?.is_custom_rank)
        ? Array.from(new Set([...competitors.map((c) => c.name).filter(Boolean), ...rankCompetitiveSet]))
        : [],
    });
  }, 1000);

  const moveAddManuallyToBottom = (options) => {
    const hasAddManually = options.some(
      (opt) => opt?.value === "Add Manually" || opt?.name === "Add Manually"
    );

    if (!hasAddManually) {
      return options;
    }

    const restOptions = options.filter(
      (opt) => opt?.value !== "Add Manually" && opt?.name !== "Add Manually"
    );

    return [
      ...restOptions,
      { name: "Add Manually", value: "Add Manually" },
    ];
  };

  const categoryOptions = useMemo(
    () => moveAddManuallyToBottom(aICategories),
    [aICategories]
  );
  const categoryTypeOptions = useMemo(() => {
    const opts = moveAddManuallyToBottom(aICategoryTypes);
    if (categoryType && opts.length > 0) {
      return [{ name: "— Clear selection —", value: "__clear__" }, ...opts];
    }
    return opts;
  }, [aICategoryTypes, categoryType]);

  const hasFormValues =
    discoveryTransactionId ||
    brand.trim() !== "" ||
    category.trim() !== "" ||
    llm !== getDefaultLLM() ||
    market.trim() !== "" ||
    selectedBrandsProducts.length > 0;


  return (
    <div className={classes.card}>
      {discoveryCtx.type !== "category" && (
        <div className={classes.header}>
          <div className={classes.radio}>
            {ENTITY_LABELS.hasProductEntity && (
              <RadioGroup
                disabled={discoveryCtx.loading}
                className={classes.type_radio}
                options={RADIO_OPTIONS}
                selectedvalue={discoveryType}
                onChange={handleDiscoveryTypeChange}
              />
            )}
            {isBaselineDiscoveryEnabled && (
              <MUISelect
                disabled={discoveryCtx.loading || discoveryTransactionId}
                options={DISCOVERY_MODE_OPTIONS}
                optionValue={discoveryMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  onDiscoveryModeChange(newMode);
                  if (newMode === "baseline") {
                    setLLM(getDefaultLLM("baseline"));
                  } else if (isSearchLLM(llm)) {
                    setLLM("gpt-5.6");
                  }
                }}
                size="small"
                style={{ minWidth: 160, marginLeft: 16 }}
              />
            )}
          </div>
          <div className={classes.header_actions}>
            {warningText && <Warning text={warningText} onClose={() => setWarningText("")} />}
            <MUIButton
              variant="outlined"
              size="small"
              onClick={() => {
                setShowHistoryModal(true)
              }}
            >
              <span style={{ marginRight: '6px', fontSize: '14px' }}>🕐</span>
              View History
            </MUIButton>
            <MUIIconButton sx={{ color: "#000", marginTop: "-10px !important" }} onClick={() => setShowConfigModal(true)}>
              <SettingsOutlined />
            </MUIIconButton>
          </div>
        </div>
      )}
      <div className={classes.input_group}>
        <div className={classes.input_section}>
          {discoveryType !== "category" && (
            <div className={classes.brand_input}>
              <TextInput
                disabled={discoveryCtx.loading || discoveryTransactionId}
                fullWidth
                value={brand}
                label={getEntityLabel(discoveryType)}
                placeholder={
                  discoveryType === "brand" ? ENTITY_LABELS.brandPlaceHolder : ENTITY_LABELS.productPlaceHolder
                }
                onChange={handleBrandInputChange}
              />
              {productVariants.length > 0 && (
                <ProductVariant
                  triggerId="basic-menu"
                  variants={productVariants}
                  index={0}
                  editable={!discoveryCtx.loading && !discoveryCtx.is_launched}
                >
                  <span className={classes.variant_text}>
                    {productVariants.length} variants found
                  </span>
                </ProductVariant>
              )}
              {loading && (
                <CircularProgress className={classes.loading} size={15} />
              )}
              {suggestions.length > 0 && (
                <span className={classes.hint_text}>
                  Did you mean:
                  <i
                    className={classes.hint_chip}
                    onClick={() => handleSuggestionClick(suggestions[0])}
                  >
                    {suggestions[0]}
                  </i>
                </span>
              )}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <MUISelect
              disabled={discoveryCtx.loading || discoveryTransactionId}
              label={"LLM"}
              loading={llmOptionsLoading}
              options={
                discoveryMode === "baseline"
                  // ? (llmOptions || []).filter((opt) => !isSearchLLM(opt.value))
                   ? getBaselineLLMOptions(llmOptions)
                  : llmOptions
              }
              optionValue={llmOptionsLoading ? null : llm}
              onChange={(e) => {
                // discoveryCtx.updateLLM(e.target.value);
                setLLM(e.target.value);
              }}
            />
          </div>

          {user?.is_language_enabled && <div style={{ flex: 0.5 }}>
            <div className="d-flex flex-column">
              <MUISelect
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                fullWidth
                optionValue={discoveryCtx.language}
                label={"Language"}
                options={discoveryCtx.languageList || []}
                onChange={(e) => {
                  resetState();
                  discoveryCtx.updateLanguage(e.target.value);
                }}
              />
            </div>
          </div>}

        </div>

        <div className={classes.input_section}>
          <div style={{ flex: 1 }}>
            <MUISelect
              loading={isCategorySuggestionLoading}
              disabled={discoveryCtx.loading || discoveryTransactionId}
              fullWidth
              placeholder={ENTITY_LABELS.categoryPlaceHolder}
              optionValue={category}
              label={ENTITY_LABELS.category}
              sx={{
                color: category ? "black" : "#979797", // Gray when placeholder is visible
              }}
              options={categoryOptions}
              onChange={(e) => {
                const value = e.target.value;
                console.log(value);
                if (value === "Add Manually") {
                  if (brand.trim().length === 0) {
                    toast.warn(`Please enter ${discoveryType} first`);
                    return;
                  }
                  setManualCategoryInput("");
                  setCategoryValidationError("");
                  setCategoryValidationSuggestion("");
                  setShowManualCategoryInput(true);
                  return;
                }
                setCategory(value);
                // setAICategoryTypes([{ name: "Add Manually", value: "Add Manually" }]);
                resetState();
              }}
            />
          </div>


          <div style={{ flex: 1 }}>
            <MUISelect
              loading={isCategoryTypeSuggestionLoading}
              disabled={discoveryCtx.loading || discoveryTransactionId}
              fullWidth
              placeholder={ENTITY_LABELS.categoryTypePlaceHolder}
              optionValue={categoryType}
              label={ENTITY_LABELS.categoryType}
              sx={{
                color: categoryType ? "black" : "#979797", // Gray when placeholder is visible
              }}
              options={categoryTypeOptions}
              onChange={(e) => {
                const value = e.target.value;
                console.log(value);
                if (value === "__clear__") {
                  setCategoryType("");
                  return;
                }
                if (value === "Add Manually") {
                  if (brand.trim().length === 0) {
                    toast.warn(`Please enter ${discoveryType} first`);
                    return;
                  }
                  setManualCategoryTypeInput("");
                  setShowManualCategoryTypeInput(true);
                  return;
                }
                setCategoryType(value);
              }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <TextInput
              fullWidth
              label={ENTITY_LABELS.market}
              disabled={discoveryCtx.loading || discoveryTransactionId}
              placeholder="e.g North America"
              value={market}
              onChange={(e) => {
                // discoveryCtx.updateMarket(e.target.value);
                setMarket(e.target.value);
                resetState();
              }}
            />
          </div>


        </div>
        {/* {discoveryCtx.type === "category" && (
            <div style={{ flex: 1 }}>
              <AttributeInput
                prefLoading={prefLoading}
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                label="Attributes you care about"
              />
            </div>
          )} */}
        <div className={classes.input_section}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <MUIMultiSelect
              loading={keyPlayersLoading}
              disabled={discoveryCtx.loading || discoveryTransactionId}
              fullWidth={true}
              optionValue={""}
              label={
                discoveryType === "category" ? "Key players " : `${ENTITY_LABELS.competitor}s `
              }
              value={
                selectedBrandsProducts?.map((obj) => obj.name) || []
              }
              customWidth={classes.multiSelectFill}
              shouldShowValue={true}
              options={discoveryTransactionId ? [] : updatedKeyPlayers}
              allowSettingPrimary={false}
              showAddManuallyOption={true}
              onChange={(e) => {
                const values = e;
                const hasAddManually = values.includes("addManually");
                if (hasAddManually) {

                  if (category.trim().length === 0) {
                    toast.warn("Please enter category first");
                    return;
                  }

                  setShowManualKeyPlayerInput(true);
                  return;
                }

                handleProductBrandSelection(values, updatedKeyPlayers);
              }}
            />
            {discoveryCtx.competitors.length < 1 &&
              category &&
              updatedKeyPlayers &&
              updatedKeyPlayers.length > 0 && (
                <Typography
                  color="textDisabled"
                  variant="caption"
                  sx={{ fontSize: "8px" }}
                >
                  Select{" "}
                  {discoveryType === "category" ? "key players " : "competitors "}
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ fontSize: "8px" }}
                  >
                    ({discoveryType === "category" ? "upto 4" : "minimum 1, upto 3"})
                  </Typography>
                </Typography>
              )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {(() => {
              const baseOptions = (userMetricSetting?.data ?? [])
                .filter((t) => {
                  if (!hasCustomIndexScoreTypes) return true;
                  return customIndexScoreType
                    ? t.custom_index_score_type === customIndexScoreType
                    : !t.custom_index_score_type;
                })
                .map((t) => ({ name: t.name, value: t.id, isDefault: t.isDefault }));
              const resolvedValue = metricSettingId === "default" ? "default" : Number(metricSettingId);
              const metricObj = discoveryCtx.metricSettingObject ?? {};
              const knownValues = new Set(baseOptions.map((o) => String(o.value)));
              const extraOptions = Object.values(metricObj)
                .filter((entry) => entry?.id != null && !knownValues.has(String(entry.id)))
                .map((entry) => ({ name: entry.name, value: entry.id === "default" ? "default" : Number(entry.id), isDefault: entry.isDefault ?? false }));
              const mergedOptions = discoveryTransactionId ? [...baseOptions, ...extraOptions] : baseOptions;
              return (
                <MUISelect
                  loading={userMetricSetting.loading}
                  disabled={discoveryCtx.loading || !!discoveryTransactionId}
                  optionValue={resolvedValue}
                  label="Template"
                  options={mergedOptions}
                  CustomOption={(opt) => (
                    <span style={{ fontWeight: opt.isDefault ? 700 : 400 }}>{opt.name}{opt.isDefault ? " ★" : ""}</span>
                  )}
                  onChange={(e) => setMetricSettingId(e.target.value)}
                />
              );
            })()}
          </div>

          <div style={{ flex: 1 }} />

        </div>
        <div className={classes.action_buttons}>
          {!discoveryCtx?.state?.id && (
            <MUIButton
              variant="contained"
              color={discoveryCtx.loading ? "error" : "primary"}
              disabled={
                loading ||
                !category ||
                // !categoryType ||
                discoveryCtx.is_launched ||
                (discoveryType !== "category" && !brand) ||
                !hasRequiredCompetitors
              }
              onClick={debounceLaunch}
            >
              Launch
            </MUIButton>
          )}
          {discoveryCtx?.state?.id && (
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              {discoveryCtx.state.transactionStatus !== "COMPLETED" && (
                <MUIButton color="error" onClick={() => setShowDeleteDialog(true)}>
                  Cancel
                </MUIButton>
              )}
            </div>
          )}
          <MUIButton
            variant="outlined"
            color="primary"
            // onClick={cancelTransactionRecord}
            onClick={handleStartNew}
            disabled={!hasFormValues}
          >
            Start New
          </MUIButton>
        </div>
      </div>
      <History
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        filter={discoveryCtx.type}
        setCancelTransaction={setCancelTransaction}
        startPolling={startPolling}
        stopPolling={stopPolling}
        isHistoryRefresh={isHistoryRefresh}
        setIsHistoryRefresh={setIsHistoryRefresh}
        page={page}
        setPage={setPage}
        onDiscoveryModeChange={onDiscoveryModeChange}
        isBaselineDiscoveryEnabled={isBaselineDiscoveryEnabled}
        onBaselineHtmlReportChange={onBaselineHtmlReportChange}
      />
      {showManualKeyPlayerInput && (
        <UserKeyPlayer
          open={showManualKeyPlayerInput}
          category={category}
          discoveryType={discoveryType}
          handleClose={() => setShowManualKeyPlayerInput(false)}
          handleConfirm={(val, domain = "example.com", variants) => {
            handleProductBrandSelection({
              id: val,
              name: val,
              domain,
              value: val,
              variants,
            });
            setShowManualKeyPlayerInput(false);
          }}
        />
      )}

      {showManualCategoryInput && (
        <Modal
          title="Add Category"
          open={true}
          showActions
          handleClose={() => {
            setShowManualCategoryInput(false);
            setCategoryValidationError("");
            setCategoryValidationSuggestion("");
          }}
          handleConfirm={() => {
            const trimmedInput = manualCategoryInput.trim();
            // Reuse an existing category (case-insensitive exact match) instead of adding a duplicate
            const existingCategory = aICategories.find(
              (c) => c.name?.trim().toLowerCase() === trimmedInput.toLowerCase()
            );
            if (!existingCategory) {
              setAICategories((prev) => [
                ...prev,
                {
                  name: trimmedInput,
                  value: trimmedInput,
                },
              ]);
            }
            resetState();
            setCategory(existingCategory ? existingCategory.value : trimmedInput);
            setShowManualCategoryInput(false);
          }}
          confirmBtnDisabled={!manualCategoryInput || categoryModalLoading}
        >
          <TextInput
            className={classes.input}
            fullWidth
            type="text"
            value={manualCategoryInput}
            onChange={(e) => {
              const value = e.target.value;
              setManualCategoryInput(value);
              debouncedCategoryValidation(value, brand, discoveryType);
            }}
            placeholder={"Enter category"}
          />
          {categoryModalLoading && <LinearProgress />}
          {categoryValidationSuggestion && (
            <span className={classes.hint_text}>
              Did you mean:
              <i
                className={classes.hint_chip}
                onClick={() => {
                  setManualCategoryInput(categoryValidationSuggestion);
                  debouncedCategoryValidation(categoryValidationSuggestion, brand, discoveryType);
                }}
              >
                {categoryValidationSuggestion}
              </i>
            </span>
          )}
          {categoryValidationError && (
            <div className="mt-3">
              <Alert
                title="Incorrect Category Name"
                message={categoryValidationError}
              />
            </div>
          )}
        </Modal>
      )}

      {showManualCategoryTypeInput && (
        <Modal
          title="Add Category Type"
          open={true}
          showActions
          handleClose={() => setShowManualCategoryTypeInput(false)}
          handleConfirm={() => {
            console.log("Confirm: " + manualCategoryTypeInput);
            setAICategoryTypes((prev) => [
              ...prev,
              {
                name: manualCategoryTypeInput,
                value: manualCategoryTypeInput,
              },
            ]);
            // resetState();
            setCategoryType(manualCategoryTypeInput);
            setShowManualCategoryTypeInput(false);
          }}
          confirmBtnDisabled={!manualCategoryTypeInput}
        >
          <TextInput
            className={classes.input}
            fullWidth
            type="text"
            value={manualCategoryTypeInput}
            onChange={(e) => setManualCategoryTypeInput(e.target.value)}
            placeholder={"Enter category Type"}
          />
        </Modal>
      )}

      {discoveryCtx?.state?.id && (
        <Modal
          handleClose={() => setShowDeleteDialog(false)}
          handleConfirm={deleteTransaction}
          open={showDeleteDialog}
          title="Delete History"
          showActions
          confirmBtnText="Delete"
          confirmBtnColor="error"
        >
          <div className={classes.contentText}>
            Do you want to delete this item - {discoveryCtx?.entity}?
          </div>
        </Modal>
      )}
      <DiscoverySettingsModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        metricSettingId={metricSettingId}
        onMetricSettingChange={setMetricSettingId}
        keywords={keywords}
        setKeywords={setKeywords}
        customIndexScoreType={customIndexScoreType}
        onCustomIndexScoreTypeChange={setCustomIndexScoreType}
        viewMode={discoveryCtx.is_launched}
        keyPlayers={keyPlayers}
        keyPlayersLoading={keyPlayersLoading}
        rankCompetitiveSet={rankCompetitiveSet}
        onRankCompetitiveSetChange={setRankCompetitiveSet}
        rankMethodology={rankMethodology}
        onRankMethodologyChange={setRankMethodology}
        mainBrand={brand}
        formCompetitors={selectedBrandsProducts.map((p) => p.name)}
        isBatchMode={isBatchMode}
        onBatchModeChange={setIsBatchMode}
        isNonBrandedQuestions={isNonBrandedQuestions}
        onNonBrandedQuestionsChange={setIsNonBrandedQuestions}
        isFactBasedQuestions={isFactBasedQuestions}
        onFactBasedQuestionsChange={setIsFactBasedQuestions}
        llm={llm}
      />
      <Modal
        title="Template Setting"
        width={"1200"}
        children={userMetricSettingCreation()}
        open={showUserMetricSettingModel}
        handleClose={() => {
          dispatch({ type: "ACTION_MODEL", payload: false });
        }}
        confirmBtnText="Add"
      />
      <ToastContainer />
    </div>
  );
}
